import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MessageSquare, Send, Loader2 } from 'lucide-react';

interface ClarificationResponseProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  notificationId: string;
  originalMessage: string;
}

export const ClarificationResponse = ({ 
  isOpen, 
  onClose, 
  reportId, 
  notificationId,
  originalMessage 
}: ClarificationResponseProps) => {
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!response.trim()) {
      toast({
        title: "Pesan kosong",
        description: "Silakan tulis informasi tambahan",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Get current report
      const { data: report, error: reportError } = await supabase
        .from('peer_reports')
        .select('description, supervisor_notes')
        .eq('id', reportId)
        .single();

      if (reportError) throw reportError;

      // Update report with clarification response and set new status
      const { error: updateError } = await supabase
        .from('peer_reports')
        .update({ 
          clarification_response: response,
          clarification_responded_at: new Date().toISOString(),
          verification_status: 'clarification_submitted'
        })
        .eq('id', reportId);

      if (updateError) throw updateError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      // Notify supervisors that clarification was provided
      // Get reporter's site_id to find relevant supervisors
      const { data: reportData } = await supabase
        .from('peer_reports')
        .select('reporter_id, location')
        .eq('id', reportId)
        .single();

      if (reportData?.reporter_id) {
        const { data: reporterProfile } = await supabase
          .from('worker_profiles')
          .select('site_id')
          .eq('id', reportData.reporter_id)
          .single();

        if (reporterProfile?.site_id) {
          const { data: supervisors } = await supabase
            .from('worker_profiles')
            .select('id')
            .eq('role', 'supervisor')
            .eq('site_id', reporterProfile.site_id)
            .limit(5);

          if (supervisors && supervisors.length > 0) {
            const notifications = supervisors.map(sup => ({
              recipient_id: sup.id,
              notification_type: 'clarification_received',
              title: 'Klarifikasi Diterima 📬',
              message: `Pekerja telah memberikan klarifikasi untuk laporan di ${reportData.location}. Silakan review.`,
              is_anonymous: false,
              data: { report_id: reportId, has_clarification: true }
            }));

            await supabase.from('notifications').insert(notifications);
          }
        }
      }

      toast({
        title: "Berhasil",
        description: "Klarifikasi telah dikirim ke supervisor"
      });

      onClose();
    } catch (error) {
      console.error('Error submitting clarification:', error);
      toast({
        title: "Error",
        description: "Gagal mengirim klarifikasi",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Berikan Informasi Tambahan
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Pesan dari Supervisor:</p>
            <p className="text-sm">{originalMessage}</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Informasi Tambahan</label>
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Jelaskan detail tambahan tentang bahaya yang dilaporkan..."
              rows={4}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Kirim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
